import React from 'react';
import { Link } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { ROUTES } from '../routes';
import fingerLogo from '../assets/partners/finger.png';
import imBankLogo from '../assets/partners/imbank.png';
import imBankHq from '../assets/partners/imbank-hq.avif';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

/**
 * Partners this deployment is branded to. Swap for Kaia later.
 * `height` is tuned per logo so the trimmed glyphs render at the same optical
 * height (finger.png fills its box; imbank.png carries ~13px of vertical padding).
 */
const PARTNERS = [
  { name: 'Finger', src: fingerLogo, height: 30 },
  { name: 'iM Bank', src: imBankLogo, height: 27 },
];

const partnerLogoTone: React.CSSProperties = { filter: 'grayscale(1) saturate(0) brightness(.68)', opacity: 0.82 };

const microEyebrow: React.CSSProperties = {
  ...mono,
  fontSize: '.6875rem',
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const h2Style: React.CSSProperties = {
  margin: 0,
  fontWeight: 500,
  fontSize: 'clamp(2rem,3.4vw,3.1rem)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--ink)',
  textWrap: 'balance',
};

const ArrowRight = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" style={{ stroke: 'currentColor', strokeWidth: 1.6 }}>
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

/** Study metadata, rendered as a spec sheet beside the outcome metrics. */
const AT_A_GLANCE: [string, string][] = [
  ['Sector', 'EVM digital-asset infrastructure'],
  ['Live chains', 'Kaia · Kairos'],
  ['Signature schemes', 'ECDSA + ML-DSA'],
  ['Account model', 'QSSN ERC-4337 smart account'],
];

/** Headline outcomes: the numbers that carry the case study. */
const HEADLINE_METRICS = [
  { value: '2.8×', label: 'Faster settlement on Kaia since the pre-optimization baseline' },
  { value: '≤ 11s', label: 'Transaction bundle submit-to-receipt on every live chain' },
  { value: '$0.001', label: 'Cost of a quantum-safe transfer on Kaia' },
  { value: 'ML-DSA', label: 'Post-quantum signature enforced on every transaction' },
];

/** The engineering decisions that made dual-signature signing work in production. */
const APPROACH = [
  {
    n: '01',
    title: 'ML-DSA verification runs off-chain',
    body: 'Post-quantum verification happens at the bundler nodes rather than in the EVM, keeping gas costs practical while still gating every privileged action behind the second signature.',
  },
  {
    n: '02',
    title: 'The proof stays on-chain and auditable',
    body: 'Signatures and public-key hashes are embedded in every transaction and emitted as event logs, so compliance bodies and third parties can independently verify that post-quantum signing was enforced, straight from a block explorer.',
  },
  {
    n: '03',
    title: 'Operator keys are retired after each bundle',
    body: 'Bundler nodes submit transactions with operator keys that are permanently retired after every bundle. No signing key is ever re-used once its public key has been exposed, extending quantum-safety to the infrastructure layer.',
  },
];

/** Latency: pre-v2 baseline vs. the current 30-bundle average. */
const LATENCY = [
  { chain: 'Kaia', baseline: '31s', avg: '~11s', p95: '~12s', delta: '65% faster', mult: '2.8×' },
  { chain: 'Kairos', baseline: '28s', avg: '~10s', p95: '~11s', delta: '64% faster', mult: '2.8×' },
];

/** Where QSSN's gas sits relative to a standard AA wallet, and where it lands with the audit trail removed. */
const GAS = [
  { label: 'Standard ERC-4337 operation', gas: '80–120k gas', note: 'A minimal operation for any ECDSA account-abstraction wallet.' },
  { label: 'QSSN, audit trail on-chain', gas: '200k gas', note: 'Adds the ML-DSA signature to calldata and an event log, so anyone can verify post-quantum signing from a block explorer.' },
  { label: 'QSSN, audit trail removed', gas: '≈ 80–120k gas', note: 'The verification overhead is optional. Drop it and gas returns to standard AA levels.', highlight: true },
];

const cellHead: React.CSSProperties = {
  background: 'var(--bg-alt)',
  padding: '.9rem 1.1rem',
  ...mono,
  fontSize: '.6rem',
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const LATENCY_COLS = '1.1fr .9fr .9fr .9fr 1.3fr';

const CaseStudies: React.FC = () => (
  <>
    <SiteNav />

    <PageHeader
      eyebrow="Case study"
      title="Making every EVM transaction quantum-safe, without breaking the chain."
      lead="The account model that makes EVM work is exactly what a quantum computer breaks first. Here is how QSSN closed that gap on live chains, and cut settlement latency by up to 65% along the way."
      aside={
        <figure style={{ margin: 0 }}>
          <div
            style={{
              borderRadius: 14,
              overflow: 'hidden',
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              boxShadow: '0 1px 2px rgba(16,18,24,.05), 0 24px 52px -42px rgba(16,18,24,.38)',
            }}
          >
            <img
              src={imBankHq}
              alt="iM Bank headquarters"
              style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'cover' }}
            />
          </div>
          <figcaption
            style={{
              ...mono,
              display: 'flex',
              alignItems: 'center',
              gap: '.75rem',
              marginTop: '.95rem',
              fontSize: '.625rem',
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>iM Bank headquarters</span>
            <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            <span style={{ whiteSpace: 'nowrap' }}>Deployment partner</span>
          </figcaption>
        </figure>
      }
    />

    {/* 01 · At a glance + headline metrics */}
    <section style={{ background: 'var(--bg-alt)', padding: '6rem 1.5rem 6.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '2.25rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>At a glance</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>01</span>
        </Reveal>

        <Reveal
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '1rem 2.25rem',
            marginBottom: '3rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>In partnership with</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2rem' }}>
            {PARTNERS.map((p) => (
              <img
                key={p.name}
                src={p.src}
                alt={`${p.name} logo`}
                style={{ display: 'block', width: 'auto', height: p.height, ...partnerLogoTone }}
              />
            ))}
          </div>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4rem', alignItems: 'flex-start' }}>
          <Reveal style={{ flex: '1 1 300px' }}>
            <div style={{ display: 'grid', gap: '1.4rem' }}>
              {AT_A_GLANCE.map(([label, value]) => (
                <div key={label} style={{ borderTop: '1px solid var(--line)', paddingTop: '.9rem' }}>
                  <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{label}</div>
                  <div style={{ margin: '.5rem 0 0', fontSize: '1.0625rem', lineHeight: 1.35, color: 'var(--ink)' }}>{value}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal
            delay={80}
            className="qssn-case-metrics"
            style={{
              flex: '1 1 560px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
              gap: 1,
              background: 'var(--line)',
              border: '1px solid var(--line)',
              borderRadius: 14,
              overflow: 'hidden',
            }}
          >
            {HEADLINE_METRICS.map((m) => (
              <div key={m.value} style={{ background: 'var(--surface)', padding: '1.7rem 1.5rem', minHeight: 168, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 'clamp(2.2rem,4vw,2.9rem)', fontWeight: 500, lineHeight: 1, letterSpacing: '-.02em', color: 'var(--accent)' }}>{m.value}</div>
                <div style={{ marginTop: 'auto', paddingTop: '1.25rem', fontSize: '.875rem', lineHeight: 1.45, color: 'var(--ink-2)' }}>{m.label}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </section>

    {/* 02 · The challenge (dark) */}
    <section style={{ background: 'var(--dark)', color: 'var(--on-dark)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--dark-line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--on-dark-2)' }}>The challenge</span>
          <span style={{ ...microEyebrow, color: 'var(--on-dark-2)' }}>02</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3.5rem 5rem', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 460px' }}>
            <Reveal as="h2" style={{ ...h2Style, color: 'var(--on-dark)' } as React.CSSProperties}>
              The weakest link in today's EVM chains is the wallet itself.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '2rem 0 0', maxWidth: '38rem', fontSize: '1.125rem', lineHeight: 1.6, color: 'var(--on-dark-2)' }}>
              The account model is built on cryptographic private keys, and that foundation falls to a sufficiently powerful quantum computer. With each transaction, a standard EOA wallet exposes its public key, and that is enough for a quantum computer to derive the private key behind it.
            </Reveal>
            <Reveal as="p" delay={120} style={{ margin: '1.25rem 0 0', maxWidth: '38rem', fontSize: '1.125rem', lineHeight: 1.6, color: 'var(--on-dark-2)' }}>
              This is not a peripheral risk. Transacting with EOA wallets is the core mechanic that makes the chain work, so the exposure is fundamental to EVM itself.
            </Reveal>
          </div>

          <Reveal delay={140} style={{ flex: '1 1 300px', borderLeft: '1px solid var(--dark-line)', paddingLeft: '2rem', alignSelf: 'center' }}>
            <div style={{ ...microEyebrow, color: 'var(--accent-on-dark)' }}>The exposure</div>
            <p style={{ margin: '1rem 0 0', fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.3, letterSpacing: '-.01em', color: 'var(--on-dark)' }}>
              Every transaction publishes the one secret a quantum attacker needs.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 03 · The approach */}
    <section style={{ background: 'var(--bg)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>Engineering</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>03 / Live-chain deployment</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4.5rem' }}>
          <div style={{ flex: '1 1 360px' }}>
            <Reveal as="h2" style={h2Style as React.CSSProperties}>
              What it took to run this on live chains.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '1.75rem 0 0', maxWidth: '32rem', fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
              The dual-signature model only helps if it stays affordable and provable in production. Three engineering decisions made post-quantum signing practical on Kaia and Kairos.
            </Reveal>
            <Reveal delay={120} style={{ marginTop: '1.6rem' }}>
              <Link
                to={ROUTES.howItWorks}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem', fontSize: '.875rem', fontWeight: 500, color: 'var(--accent)', textDecoration: 'none' }}
              >
                See how the dual signature works
                <ArrowRight />
              </Link>
            </Reveal>
          </div>

          <Reveal style={{ flex: '1 1 460px' }}>
            {APPROACH.map((s) => (
              <div key={s.n} style={{ display: 'grid', gridTemplateColumns: '4rem 1fr', gap: '1.5rem', padding: '1.9rem 0', borderTop: '1px solid var(--line)' }}>
                <span style={{ ...mono, fontSize: '.875rem', color: 'var(--accent)' }}>{s.n}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.2, color: 'var(--ink)', letterSpacing: '-.01em' }}>{s.title}</h3>
                  <p style={{ margin: '.85rem 0 0', lineHeight: 1.6, color: 'var(--ink-2)' }}>{s.body}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </section>

    {/* 04 · Measured impact (latency) */}
    <section style={{ background: 'var(--bg-alt)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>Measured impact</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>04 / Latency</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem 4rem', alignItems: 'flex-end', marginBottom: '3rem' }}>
          <Reveal as="h2" style={{ ...h2Style, flex: '1 1 420px' } as React.CSSProperties}>
            Quantum-safe and faster than the baseline it replaced.
          </Reveal>
          <Reveal as="p" style={{ flex: '1 1 380px', margin: 0, fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
            Restructuring how the rotating operator wallets are funded, alongside broader tuning, cut end-to-end latency across every live chain. Figures are net bundle submit-to-receipt times, averaged over the last 30 bundles.
          </Reveal>
        </div>

        <Reveal style={{ border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden' }}>
          <div className="qssn-threat-table-head" style={{ display: 'grid', gridTemplateColumns: LATENCY_COLS, background: 'var(--line)', gap: 1 }}>
            <div style={{ ...cellHead, color: 'var(--ink-3)' }}>Chain</div>
            <div style={{ ...cellHead, color: 'var(--ink-3)' }}>Baseline</div>
            <div style={{ ...cellHead, color: 'var(--ink-3)' }}>Avg now</div>
            <div style={{ ...cellHead, color: 'var(--ink-3)' }}>p95</div>
            <div style={{ ...cellHead, color: 'var(--accent)' }}>Improvement</div>
          </div>
          <div style={{ display: 'grid', background: 'var(--line)', gap: 1 }}>
            {LATENCY.map((row) => (
              <div key={row.chain} className="qssn-threat-table-row" style={{ display: 'grid', gridTemplateColumns: LATENCY_COLS, gap: 1 }}>
                <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '1.1rem 1.1rem', fontSize: '1.0625rem', fontWeight: 500, color: 'var(--ink)' }}>
                  <span className="qssn-preview-cell-label">Chain</span>
                  {row.chain}
                </div>
                <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '1.1rem', ...mono, fontSize: '.9375rem', color: 'var(--ink-3)' }}>
                  <span className="qssn-preview-cell-label">Baseline</span>
                  {row.baseline}
                </div>
                <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '1.1rem', ...mono, fontSize: '.9375rem', color: 'var(--ink)' }}>
                  <span className="qssn-preview-cell-label">Avg now</span>
                  {row.avg}
                </div>
                <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '1.1rem', ...mono, fontSize: '.9375rem', color: 'var(--ink-2)' }}>
                  <span className="qssn-preview-cell-label">p95</span>
                  {row.p95}
                </div>
                <div className="qssn-threat-table-cell" style={{ background: 'var(--surface)', padding: '1.1rem', display: 'flex', alignItems: 'baseline', gap: '.6rem' }}>
                  <span className="qssn-preview-cell-label">Improvement</span>
                  <span style={{ fontSize: '1.0625rem', fontWeight: 500, color: 'var(--accent)' }}>{row.delta}</span>
                  <span style={{ ...mono, fontSize: '.8125rem', color: 'var(--ink-3)' }}>{row.mult}</span>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal as="p" delay={80} style={{ margin: '1.5rem 0 0', maxWidth: '46rem', fontSize: '.875rem', lineHeight: 1.55, color: 'var(--ink-3)' }}>
          p95 is the 95th percentile: 95% of bundles complete within that time. Each bundle currently carries a single UserOp, so these are also per-transaction times today. Baseline reflects performance before the operator-wallet funding restructure. Further gains are in progress, including an enhancement expected to cut roughly 0.5s from bundle time on every chain.
        </Reveal>
      </div>
    </section>

    {/* 05 · The economics (gas) */}
    <section style={{ background: 'var(--bg)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>The economics</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>05 / Gas</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4.5rem' }}>
          <div style={{ flex: '1 1 360px' }}>
            <Reveal as="h2" style={h2Style as React.CSSProperties}>
              The overhead is the audit trail, and it is optional.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '1.75rem 0 0', maxWidth: '32rem', fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
              On Kaia, a quantum-safe QSSN transfer costs about $0.001, and its gas footprint sits in line with account-abstracted wallets generally, not as an outlier.
            </Reveal>
            <Reveal as="p" delay={120} style={{ margin: '1.25rem 0 0', maxWidth: '32rem', fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
              Almost all of the gap over a standard AA setup is the ML-DSA signature we place in calldata and emit as an event log, so compliance bodies can verify post-quantum signing from a block explorer without parsing raw calldata. That audit trail is optional: parts or all of it can be removed to bring QSSN in line with typical AA costs.
            </Reveal>
          </div>

          <Reveal style={{ flex: '1 1 440px', display: 'grid', gap: 1, background: 'var(--line)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden', alignSelf: 'flex-start' }}>
            <div style={{ background: 'var(--surface)', padding: '1.6rem 1.5rem' }}>
              <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>Cost on Kaia today</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '.6rem', marginTop: '.55rem' }}>
                <span style={{ fontSize: 'clamp(2.2rem,4vw,2.9rem)', fontWeight: 500, lineHeight: 1, letterSpacing: '-.02em', color: 'var(--accent)' }}>$0.001</span>
                <span style={{ ...mono, fontSize: '.8125rem', color: 'var(--ink-3)' }}>≈ 0.005 KAIA</span>
              </div>
              <p style={{ margin: '.7rem 0 0', fontSize: '.875rem', lineHeight: 1.5, color: 'var(--ink-2)' }}>What a quantum-safe transfer costs per transaction, negligible in absolute terms.</p>
            </div>
            {GAS.map((row) => (
              <div key={row.label} style={{ background: row.highlight ? 'var(--accent-soft)' : 'var(--surface)', padding: '1.4rem 1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem' }}>
                  <span style={{ fontSize: '1.0625rem', fontWeight: 500, color: 'var(--ink)' }}>{row.label}</span>
                  <span style={{ ...mono, fontSize: '1.0625rem', fontWeight: 500, color: row.highlight ? 'var(--accent)' : 'var(--ink)', whiteSpace: 'nowrap' }}>{row.gas}</span>
                </div>
                <p style={{ margin: '.6rem 0 0', fontSize: '.875rem', lineHeight: 1.5, color: 'var(--ink-2)' }}>{row.note}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </section>

    {/* 06 · The outcome + CTA */}
    <section style={{ background: 'var(--bg-alt)', padding: '6.5rem 1.5rem 7rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>The outcome</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>06</span>
        </Reveal>

        <Reveal as="p" style={{ margin: 0, maxWidth: '52rem', fontSize: 'clamp(1.4rem,2.6vw,2.1rem)', fontWeight: 500, lineHeight: 1.25, letterSpacing: '-.01em', color: 'var(--ink)' }}>
          QSSN made iM Bank quantum-safe at the transaction level.
        </Reveal>
        <Reveal as="p" delay={60} style={{ margin: '1.5rem 0 0', maxWidth: '46rem', fontSize: '1.125rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
          Every transfer is co-signed with a post-quantum key, settles in seconds for about $0.001, and stays provable on-chain for auditors. No fork, no token migration, and no change to how the bank already operates.
        </Reveal>

        <Reveal
          delay={80}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1.5rem 3rem',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '3.5rem',
            paddingTop: '2rem',
            borderTop: '1px solid var(--line)',
          }}
        >
          <p style={{ flex: '1 1 480px', margin: 0, fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
            Exploring quantum-safe controls for your institution? We'll walk you through what a deployment looks like.
          </p>
          <Link
            to={ROUTES.contact}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.55rem',
              background: 'var(--accent)',
              color: '#fff',
              textDecoration: 'none',
              fontSize: '.9375rem',
              fontWeight: 500,
              padding: '.85rem 1.4rem',
              borderRadius: 9,
              whiteSpace: 'nowrap',
            }}
          >
            Get in touch
            <ArrowRight />
          </Link>
        </Reveal>
      </div>
    </section>

    <Footer />
  </>
);

export default CaseStudies;
